import { useEffect, useState } from "react";
import { apiFetchBlob } from "@/lib/api";
import { getInvoicePdfUrl } from "@/lib/invoices-payments";
import { cn } from "@/lib/utils";

export interface InvoicePdfFrameProps {
  invoiceId: number;
  className?: string;
  title?: string;
}

export function InvoicePdfFrame({ invoiceId, className, title }: InvoicePdfFrameProps) {
  // The PDF endpoint requires a bearer token, which <iframe src> / <a href download>
  // cannot attach. Fetch the bytes via apiFetchBlob, then expose them through a
  // blob: URL that both elements can consume.
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    setError(null);
    setBlobUrl(null);

    apiFetchBlob(getInvoicePdfUrl(invoiceId))
      .then((blob) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [invoiceId]);

  return (
    <div className={cn("flex flex-col gap-2 sticky top-6", className)}>
      {blobUrl ? (
        <iframe
          title={title ?? "Invoice PDF"}
          src={blobUrl}
          className="w-full h-[calc(100vh-10rem)] min-h-[480px] max-h-[860px] border rounded-md bg-white"
        />
      ) : (
        <div
          title={title ?? "Invoice PDF"}
          role="status"
          className="w-full h-[calc(100vh-10rem)] min-h-[480px] max-h-[860px] border rounded-md bg-white flex items-center justify-center text-sm text-muted-foreground"
        >
          {error ? `Failed to load PDF: ${error}` : "Loading PDF…"}
        </div>
      )}
      {blobUrl && (
        <a
          href={blobUrl}
          download={`invoice-${invoiceId}.pdf`}
          className="text-sm text-primary underline self-start"
        >
          Download
        </a>
      )}
    </div>
  );
}
